import { ErrorModal } from "@dexteel/mesf-core";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import {
	useAccountSummary,
	useCloseDates,
	useCostCenters,
} from "../hooks/useSummaryByCloseDate";
import { toCloseDateOptions } from "../models/SummaryByCloseDate";
import { AccountSummaryGrid } from "./AccountSummaryGrid";
import { SummaryByCloseDateToolbar } from "./SummaryByCloseDateToolbar";

// An empty state fills the space the sheet would take, instead of leaving a short card
// floating over the background with the page half grey underneath it.
const FILLER_SX = {
	flex: 1,
	minHeight: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;

const URL_SCHEMA = {
	costCenterId: urlParam.int(),
	closeDate: urlParam.string(),
};

// The procedure speaks MM/DD/YYYY; the URL carries the date as YYYY-MM-DD like every
// other page.
const toUrlDate = (value: string | null) => {
	const [month, day, year] = value?.split("/") ?? [];
	return year && month && day ? `${year}-${month}-${day}` : null;
};
const fromUrlDate = (value: string | null) => {
	const [year, month, day] = value?.split("-") ?? [];
	return year && month && day ? `${month}/${day}/${year}` : null;
};

export const SummaryByCloseDateReport = () => {
	const [{ costCenterId, closeDate: urlCloseDate }, setFilters] =
		useUrlState(URL_SCHEMA);
	const closeDate = fromUrlDate(urlCloseDate);
	const [errorMsg, setErrorMsg] = useState("");

	const costCentersQuery = useCostCenters();
	const closeDatesQuery = useCloseDates(costCenterId);
	const summaryQuery = useAccountSummary(costCenterId, closeDate);

	const closeDates = useMemo(
		() => toCloseDateOptions(closeDatesQuery.data ?? []),
		[closeDatesQuery.data],
	);

	const handleCostCenterChange = (value: number | null) => {
		// The close dates belong to the cost center, so the one on screen no longer
		// means anything.
		setFilters({ costCenterId: value, closeDate: null });
	};

	const setCloseDate = (value: string | null) =>
		setFilters({ closeDate: toUrlDate(value) });

	// A shared link can name a cost center or a close date that is not offered.
	useEffect(() => {
		if (!costCentersQuery.isSuccess || costCenterId == null) return;
		if (!costCentersQuery.data.some((cc) => cc.Id === costCenterId))
			setFilters({ costCenterId: null, closeDate: null });
	}, [
		costCentersQuery.isSuccess,
		costCentersQuery.data,
		costCenterId,
		setFilters,
	]);

	useEffect(() => {
		if (urlCloseDate == null) return;
		if (costCenterId == null) {
			setFilters({ closeDate: null });
			return;
		}
		if (
			closeDatesQuery.isSuccess &&
			(!closeDate || !closeDates.includes(closeDate))
		)
			setFilters({ closeDate: null });
	}, [
		closeDatesQuery.isSuccess,
		closeDates,
		closeDate,
		urlCloseDate,
		costCenterId,
		setFilters,
	]);

	const error =
		costCentersQuery.error ?? closeDatesQuery.error ?? summaryQuery.error;
	if (error && !errorMsg) setErrorMsg((error as Error).message);

	const summary = summaryQuery.data ?? null;
	const selectionComplete = costCenterId != null && !!closeDate;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				// The app navbar is 64px tall and nothing above this Box reserves height, so
				// the page has to claim the rest itself.
				height: "calc(100vh - 64px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Summary By Close Date
			</Typography>

			<Paper elevation={3}>
				<SummaryByCloseDateToolbar
					costCenters={costCentersQuery.data ?? []}
					costCenterId={costCenterId}
					onCostCenterChange={handleCostCenterChange}
					closeDates={closeDates}
					closeDate={closeDate}
					onCloseDateChange={setCloseDate}
					closeDatesLoading={closeDatesQuery.isInitialLoading}
				/>
			</Paper>

			{!selectionComplete && (
				<Paper elevation={3} sx={FILLER_SX}>
					<Typography variant="body1" color="text.secondary">
						Pick a cost center and a close date
					</Typography>
				</Paper>
			)}

			{selectionComplete && summaryQuery.isInitialLoading && (
				<Paper elevation={3} sx={FILLER_SX}>
					<CircularProgress />
				</Paper>
			)}

			{selectionComplete && !summaryQuery.isInitialLoading && !summary && (
				<Paper elevation={3} sx={FILLER_SX}>
					<Typography variant="body1" color="text.secondary">
						No summary for the selected cost center and close date
					</Typography>
				</Paper>
			)}

			{selectionComplete && !summaryQuery.isInitialLoading && summary && (
				<AccountSummaryGrid summary={summary} />
			)}

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
